import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { cn } from '@/utils';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

interface MediaItem {
  media_id: string;
  media_type: string;
  url: string;
  thumbnail_url?: string;
  medium_url?: string;
  large_url?: string;
  width?: number;
  height?: number;
  description?: string;
  alt_text?: string;
}

interface ImageGalleryProps {
  media?: MediaItem[];
  mediaUrls?: string[];
  alt?: string;
  className?: string;
}

export function ImageGallery({ media, mediaUrls, alt, className }: ImageGalleryProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Convert mediaUrls to media items if provided
  const mediaItems: MediaItem[] = media || (mediaUrls?.map((url, index) => ({
    media_id: `img-${index}`,
    media_type: 'photo',
    url: url,
    medium_url: url,
    large_url: url,
    alt_text: alt || `Image ${index + 1}`,
  })) || []);
  
  const images = mediaItems.filter(m => m.media_type === 'photo');
  const videos = mediaItems.filter(m => m.media_type === 'video');
  const gifs = mediaItems.filter(m => m.media_type === 'animated_gif');
  
  if (images.length === 0 && videos.length === 0 && gifs.length === 0) {
    return null;
  }
  
  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };
  
  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };
  
  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };
  
  // Determine grid layout based on image count
  const getGridClass = () => {
    const count = images.length;
    if (count === 1) return 'grid-cols-1';
    if (count === 2) return 'grid-cols-2';
    if (count === 3) return 'grid-cols-2 grid-rows-2';
    return 'grid-cols-2';
  };
  
  const getImageClass = (index: number) => {
    const count = images.length;
    if (count === 1) return 'aspect-video';
    if (count === 3 && index === 0) return 'col-span-1 row-span-2 aspect-[3/4]';
    return 'aspect-square';
  };
  
  return (
    <div className={cn('space-y-2', className)}>
      {/* Images Grid */}
      {images.length > 0 && (
        <div className={cn('grid gap-1 rounded-lg overflow-hidden', getGridClass())}>
          {images.slice(0, 4).map((image, index) => (
            <button
              key={image.media_id}
              onClick={() => openLightbox(index)}
              className={cn(
                'relative group overflow-hidden bg-muted cursor-pointer',
                getImageClass(index)
              )}
            >
              <img
                src={image.medium_url || image.url}
                alt={image.alt_text || image.description || `Image ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              
              {/* Show more indicator */}
              {images.length > 4 && index === 3 && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="text-white text-xl font-semibold">
                    +{images.length - 4}
                  </span>
                </div>
              )}
            </button>
          ))}
        </div>
      )}
      
      {/* Videos */}
      {videos.map((video) => (
        <div key={video.media_id} className="relative rounded-lg overflow-hidden bg-black">
          <video
            src={video.url}
            poster={video.thumbnail_url}
            controls
            className="w-full max-h-[500px]"
            preload="metadata"
          >
            Your browser does not support the video tag.
          </video>
          {video.description && (
            <p className="text-xs text-muted-foreground mt-1 p-2">
              {video.description}
            </p>
          )}
        </div>
      ))}
      
      {/* GIFs */}
      {gifs.map((gif) => (
        <div key={gif.media_id} className="relative rounded-lg overflow-hidden">
          <video
            src={gif.url}
            poster={gif.thumbnail_url}
            autoPlay
            loop
            muted
            playsInline
            className="w-full max-h-[400px]"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      ))}
      
      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
          <div className="relative flex items-center justify-center min-h-[50vh]">
            {/* Close button */}
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Navigation */}
            {images.length > 1 && (
              <>
                <button
                  onClick={goToPrevious}
                  className="absolute left-4 z-50 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-4 z-50 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
            
            {/* Image */}
            {images[currentIndex] && (
              <div className="flex flex-col items-center">
                <img
                  src={images[currentIndex].large_url || images[currentIndex].url}
                  alt={images[currentIndex].alt_text || ''}
                  className="max-w-full max-h-[80vh] object-contain"
                />
                
                {/* Image info */}
                {(images[currentIndex].description || images[currentIndex].alt_text) && (
                  <div className="mt-4 px-4 py-2 bg-black/50 rounded-lg max-w-2xl">
                    <p className="text-white text-sm text-center">
                      {images[currentIndex].description || images[currentIndex].alt_text}
                    </p>
                  </div>
                )}
                
                {/* Counter */}
                {images.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/50 rounded-full">
                    <span className="text-white text-sm">
                      {currentIndex + 1} / {images.length}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
